import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { _isFeDev, _log } from '@shared/aux_helper_environment';
import { _formatSqlQuery, _timeout } from '@shared/aux_helper_functions';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AiDashboardErrorDetailsModalComponent } from 'app/ai-dashboard/components/ai-dashboard-error-details-modal/ai-dashboard-error-details-modal.component';
import { GenieStatementResponse } from 'app/services/agents/databricks-genie-ai-chat.models';
import {
  DataBricksAiGenieChatResponse,
  DataBricksAiGenieChatService,
  GenieError,
  GenieErrorType,
} from 'app/services/agents/databricks-genie-ai-chat.service';
import { dummyDataBricksAiGenieChatResponse } from 'app/services/agents/databricks-genie-ai-dummyData';
import { IanTranslateService, PrismaDynamicEnv } from 'core/services/ian-core-singleton.service';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatContentComponent, ChatMessage, ChatType } from './models/chat-interface';

interface PromptExample {
  textKey?: string; // Clave de traducción (para backward compatibility)
  text?: string; // Texto directo del prompt
  editable?: boolean; // Si es true, copia al input sin enviar
  icon?: string;
}

@Component({
  selector: 'databricks-chat-content',
  template: `
    <div class="databricks-chat-content">
      <button
        mat-icon-button
        [matMenuTriggerFor]="promptExamplesMenu"
        class="prompt-examples-trigger"
        [matTooltip]="'AI_CHAT.PROMPT_EXAMPLES.TITLE' | pTranslate"
      >
        <mat-icon>auto_awesome</mat-icon>
      </button>

      <mat-menu #promptExamplesMenu="matMenu" class="prompt-examples-menu-panel">
        <div class="menu-header" (click)="$event.stopPropagation()">
          <mat-icon>auto_awesome</mat-icon>
          <span>{{ 'AI_CHAT.PROMPT_EXAMPLES.TITLE' | pTranslate }}</span>
        </div>
        <button mat-menu-item *ngFor="let example of promptExamples" (click)="sendExamplePrompt(example)">
          <!-- <mat-icon class="prompt-icon">auto_awesome</mat-icon> -->
          <span>{{ getPromptText(example) }}</span>
        </button>

        <ng-container *ngIf="recentQueries.length > 0">
          <div class="menu-header" (click)="$event.stopPropagation()">
            <mat-icon>history</mat-icon>
            <span>{{ 'AI_CHAT.PROMPT_EXAMPLES.RECENT_QUERIES' | pTranslate }}</span>
          </div>
          <button mat-menu-item *ngFor="let query of recentQueries" (click)="sendExamplePrompt(query)">
            <!-- <mat-icon class="prompt-icon">history</mat-icon> -->
            <span>{{ query.text }}</span>
          </button>
        </ng-container>
      </mat-menu>

      <div class="messages" #messagesContainer>
        <div
          *ngFor="let message of messages; let isFirst = first"
          [ngClass]="{
            'from-user': message.from === 'user',
            'from-ai': message.from === 'ai',
            'has-query-result': hasValidSchemaQueryResult(message.queryProcessResult)
          }"
          class="message"
        >
          <div class="message-header">
            <ng-container *ngIf="message.from !== 'user'">
              <ai-icon [size]="20"></ai-icon>
            </ng-container>
            {{
              message.from === 'user'
                ? userDataName || ('AI_CHAT.MESSAGES.USER_DEFAULT' | pTranslate)
                : ('AI_CHAT.MESSAGES.AI_NAME' | pTranslate)
            }}
          </div>
          <div [innerHTML]="message.text"></div>
          <!-- Botón de detalles técnicos si hay error -->
          <div *ngIf="message.error && message.errorDetails" style="margin-top: 12px;">
            <button mat-raised-button color="primary" (click)="openErrorDetailsModal(message.errorDetails)" style="font-size: 13px;">
              Ver detalles técnicos
            </button>
          </div>

          <!-- Query result display - Using original query-process-result component -->
          <div *ngIf="hasValidSchemaQueryResult(message.queryProcessResult)" class="queryProcessResultWrapp">
            <div class="queryProcessResult-header">{{ 'AI_CHAT.QUERY_RESULTS.HEADER' | pTranslate }}</div>
            <div class="queryProcessResult-content">
              <query-process-result
                *ngIf="hasValidQueryResult(message.queryProcessResult)"
                [queryProcessResult]="message.queryProcessResult"
                [message]="message"
                [showPinButton]="true"
              ></query-process-result>
              <div *ngIf="!hasValidQueryResult(message.queryProcessResult)" class="no-data-message">
                <span>{{ 'AI_CHAT.QUERY_RESULTS.NO_DATA' | pTranslate }}</span>
                <button
                  *ngIf="hasQuery(message)"
                  mat-icon-button
                  [matMenuTriggerFor]="queryPopover"
                  [matTooltip]="'AI_CHAT.QUERY_RESULTS.VIEW_QUERY' | pTranslate"
                  #queryBtn="matTooltip"
                >
                  <mat-icon>code</mat-icon>
                </button>
                <mat-menu #queryPopover="matMenu" class="query-popover">
                  <div class="query-container">
                    <pre class="query-content">{{ getQuery(message) }}</pre>
                    <button
                      mat-icon-button
                      class="copy-button"
                      (click)="copyQuery(message)"
                      [matTooltip]="'AI_CHAT.QUERY_RESULTS.COPY_QUERY' | pTranslate"
                    >
                      <mat-icon>content_copy</mat-icon>
                    </button>
                  </div>
                </mat-menu>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="loading" class="message from-ai loading-message">
          <div class="loading-indicator">
            <div class="loadingWrapp">
              <mat-spinner color="accent" diameter="15"></mat-spinner>
            </div>
            {{ 'AI_CHAT.MESSAGES.LOADING' | pTranslate }}
          </div>
        </div>
      </div>

      <!-- Prompt Examples Fixed Bar (arriba del input) -->
      <div *ngIf="showPromptExamples" class="prompt-examples-fixed-bar">
        <div class="prompt-examples-grid">
          <button
            *ngFor="let example of promptExamples"
            class="prompt-example-card"
            (click)="sendExamplePrompt(example)"
            [disabled]="loading"
          >
            <mat-icon class="prompt-icon">auto_awesome</mat-icon>
            <span class="prompt-example-text">{{ getPromptText(example) }}</span>
          </button>
        </div>
      </div>

      <chat-input
        [(value)]="userInput"
        [placeholder]="'AI_CHAT.INPUT.PLACEHOLDER' | pTranslate"
        [disabled]="loading"
        [rows]="2"
        [resetTooltip]="'AI_CHAT.INPUT.RESET_TOOLTIP' | pTranslate"
        [hasConversation]="messages && messages.length > 1"
        [showChatTypeSelector]="showChatTypeSelector"
        [selectedChatType]="selectedChatType"
        [availableChatTypes]="availableChatTypes"
        (send)="onSendMessage($event)"
        (reset)="resetConversation()"
        (chatTypeChange)="onChatTypeChange($event)"
      ></chat-input>
    </div>
  `,
  styleUrls: ['./databricks-chat-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DatabricksChatContentComponent implements OnInit, OnDestroy, ChatContentComponent {
  @ViewChild('messagesContainer') private messagesContainer: ElementRef;

  @Output() onResetRequest = new EventEmitter<void>();
  @Output() chatTypeChange = new EventEmitter<ChatType>(); // Nuevo output
  @Input() isInPanelContext: boolean;
  @Input() showChatTypeSelector = false;
  @Input() selectedChatType: ChatType = ChatType.DATABRICKS;
  @Input() availableChatTypes: { value: ChatType; label: string; description: string }[] = [];

  messages: ChatMessage[] = null;
  userInput = '';
  loading = false;
  apiUrl = null;
  userDataName = null;
  chatPermision = null;

  // Control de error y estado
  hasError = false;
  errorMessage = '';

  // Ejemplos de prompts
  promptExamples: PromptExample[] = [
    { textKey: 'AI_CHAT.PROMPT_EXAMPLES.TOP_PRODUCTS' },
    { textKey: 'AI_CHAT.PROMPT_EXAMPLES.SALES_COMPARISON' },
    { textKey: 'AI_CHAT.PROMPT_EXAMPLES.INVENTORY_ALERT' },
    { textKey: 'AI_CHAT.PROMPT_EXAMPLES.MONTHLY_TREND' },
  ];
  recentQueries: PromptExample[] = [];
  showPromptExamples = true; // Mostrar ejemplos por defecto

  private get LS_KEY_VERSIONED() {
    const _keyVer = environment.localStorageKeyVerPrefix || '_v3_';
    return `${_keyVer}databricks_recent_queries`;
  }

  private _unsubscribeAll: Subject<any>;

  constructor(
    private sanitizer: DomSanitizer,
    private oidcSecurityService: OidcSecurityService,
    private dataBricksAiGenieChatService: DataBricksAiGenieChatService,
    private translate: IanTranslateService,
    private dialog: MatDialog,
    private prismaDynamicEnv: PrismaDynamicEnv
  ) {
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    _log('Lakehouse Chat Content - ngOnInit');

    // Cargar prompts desde configuración o usar defaults
    this.loadPromptExamples();

    // Initialize chat regardless of context
    this.initializeChat();

    // Cargar consultas recientes desde localStorage
    this.loadRecentQueries();
  }

  private loadRecentQueries(): void {
    try {
      const stored = localStorage.getItem(this.LS_KEY_VERSIONED);
      if (stored) {
        const queries: string[] = JSON.parse(stored);
        this.recentQueries = queries.map(text => ({ text, editable: true }));
      }
    } catch (e) {
      _log('[loadRecentQueries] Error loading from localStorage', e);
    }
  }

  private saveQueryToHistory(query: string): void {
    if (!query || query.trim().length === 0) return;

    try {
      const q = query.trim();

      // Obtener actuales
      let queries: string[] = [];
      const stored = localStorage.getItem(this.LS_KEY_VERSIONED);

      if (stored) queries = JSON.parse(stored);

      // Evitar duplicados y limitar a 24
      queries = queries.filter(item => item.toLowerCase() !== q.toLowerCase());
      queries.unshift(q);
      queries = queries.slice(0, 24);

      localStorage.setItem(this.LS_KEY_VERSIONED, JSON.stringify(queries));

      // Actualizar vista
      this.recentQueries = queries.map(text => ({ text, editable: true }));
    } catch (e) {
      _log('[saveQueryToHistory] Error saving to localStorage', e);
    }
  }

  private loadPromptExamples(): void {
    // Prompts por defecto del diccionario (siempre se usan como base)
    const defaultPrompts: PromptExample[] = [
      { textKey: 'AI_CHAT.PROMPT_EXAMPLES.TOP_PRODUCTS', editable: false },
      { textKey: 'AI_CHAT.PROMPT_EXAMPLES.SALES_COMPARISON', editable: false },
      { textKey: 'AI_CHAT.PROMPT_EXAMPLES.INVENTORY_ALERT', editable: false },
      { textKey: 'AI_CHAT.PROMPT_EXAMPLES.MONTHLY_TREND', editable: false },
    ];

    // Intentar cargar prompts desde configuración de ambiente
    const configPrompts = this.prismaDynamicEnv.getConf('brandCustomization.dataBricksGenieConfig.geniePrompts');

    if (configPrompts && Array.isArray(configPrompts) && configPrompts.length > 0) {
      // Los prompts de configuración pisan los primeros del diccionario por orden
      const customPrompts = configPrompts.map(prompt => ({
        text: prompt.text,
        editable: prompt.editable || false,
      }));

      // Reemplazar los primeros N prompts del diccionario con los de configuración
      this.promptExamples = [...customPrompts, ...defaultPrompts.slice(customPrompts.length)];

      _log('[loadPromptExamples] Pisando primeros prompts con configuración:', {
        custom: customPrompts.length,
        fromDict: defaultPrompts.length - customPrompts.length,
        total: this.promptExamples,
      });
    } else {
      // Usar todos los prompts por defecto del diccionario
      this.promptExamples = defaultPrompts;
      _log('[loadPromptExamples] Usando todos los prompts del diccionario');
    }
  }

  private initializeChat(): void {
    this.resetConversation();

    // Verificar permisos del chat
    const initialPermission = this.isAssistantEnabledAiAgent();

    if (!initialPermission) {
      this.handleServiceUnavailable(new Error('Configuración del servicio de IA no disponible'));
      return;
    }

    // Obtener datos del usuario
    this.oidcSecurityService
      .getUserData()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(userData => {
        if (userData?.name) this.userDataName = userData?.name;
      });

    if (false && _isFeDev()) this.fakeMirstMsg();
  }

  isAssistantEnabledAiAgent() {
    // Verificar configuración básica primero
    if (!this.dataBricksAiGenieChatService.CAN_USE_DB_CHAT()) {
      return false;
    }

    // Verificar conectividad con el servicio
    this.dataBricksAiGenieChatService.getServiceHealth().subscribe({
      next: response => {
        _log('[isAssistantEnabledAiAgent] ✅ Servicio de salud verificado:', response);
        this.chatPermision = true;
      },
      error: error => {
        _log('[isAssistantEnabledAiAgent] ❌ Error en verificación de salud:', error);
        this.chatPermision = false;
        this.handleServiceUnavailable(error);
      },
    });

    return this.dataBricksAiGenieChatService.CAN_USE_DB_CHAT();
  }

  private handleServiceUnavailable(error: any) {
    const errorMessage = {
      from: 'ai' as const,
      text: this.sanitizer.bypassSecurityTrustHtml(
        `<div class="error-message">
          <strong>${this.translate.instant('AI_CHAT.ERRORS.SERVICE_UNAVAILABLE')}</strong><br>
          ${this.translate.instant('AI_CHAT.ERRORS.SERVICE_UNAVAILABLE_DESC')}
        </div>`
      ),
      error: GenieErrorType.NETWORK,
    };

    if (this.messages && this.messages.length > 0) {
      this.messages.push(errorMessage);
      this.scrollToBottomWhitDelay();
    }
  }

  resetConversation() {
    this.messages = [{ from: 'ai', text: this.translate.instant('AI_CHAT.MESSAGES.DATABRICKS_WELCOME') }];
    this.userInput = '';
    this.hasError = null;
    this.errorMessage = '';
    this.showPromptExamples = true; // Mostrar ejemplos de nuevo al resetear
    this.dataBricksAiGenieChatService.resetConversation();
    this.onResetRequest.emit();
  }

  async scrollToBottomWhitDelay($time = 100) {
    await _timeout($time);
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
    } catch (err) {
      console.error(err);
    }
  }

  async sendMessage() {
    if (!this.userInput.trim()) return;
    await this.onSendMessage(this.userInput.trim());
  }

  /**
   * Obtiene el texto a mostrar para un prompt de ejemplo
   * Prioriza 'text' sobre 'textKey' para mantener backward compatibility
   */
  getPromptText(example: PromptExample): string {
    if (example.text) {
      return example.text;
    } else if (example.textKey) {
      return this.translate.instant(example.textKey);
    }
    return '';
  }

  async sendExamplePrompt(example: PromptExample) {
    // Obtener el texto del prompt
    let promptText: string;

    // Priorizar 'text' sobre 'textKey' para nuevos prompts configurables
    if (example.text) {
      promptText = example.text;
    } else if (example.textKey) {
      // Backward compatibility: usar textKey con traducción
      promptText = this.translate.instant(example.textKey);
      // Si la traducción no existe, usar la key como fallback
      if (promptText === example.textKey) {
        _log('[sendExamplePrompt] ⚠️ Translation key not found:', example.textKey);
      }
    }

    if (!promptText) {
      _log('[sendExamplePrompt] ⚠️ No prompt text available');
      return;
    }

    // Si es editable, copiar al input sin enviar
    if (example.editable) {
      this.userInput = promptText;
      _log('[sendExamplePrompt] Prompt copiado al input (editable):', promptText);
    } else {
      // Si no es editable, enviar directamente
      await this.onSendMessage(promptText, true);
      _log('[sendExamplePrompt] Prompt enviado directamente:', promptText);
    }
  }

  async onSendMessage(message: string, isFromSuggestion = false) {
    if (!message.trim()) return;

    // Si es el primer mensaje del chat y no viene de una sugerencia, guardarlo en el historial
    if (this.messages.length === 1 && !isFromSuggestion) {
      this.saveQueryToHistory(message);
    }

    // Ocultar los ejemplos de prompt cuando el usuario envía un mensaje
    this.showPromptExamples = false;

    this.messages.push({ from: 'user', text: message });
    this.userInput = '';
    this.loading = true;
    this.hasError = false;

    await this.scrollToBottomWhitDelay(50);

    this.dataBricksAiGenieChatService.sendGenieMessage(message).subscribe({
      next: (response: DataBricksAiGenieChatResponse) => {
        _log('LakehouseChatContentComponent response', response);

        if (!response || response.error) {
          // Pasar el response completo si tiene 'text', sino solo el error
          this.handleError(response?.text ? response : response?.error);
          return;
        }

        this.putMsg(response);
      },
      error: error => {
        this.handleError(error);
      },
    });
  }

  putMsg(response: DataBricksAiGenieChatResponse) {
    this.loading = false;

    const messageContent = {
      from: 'ai' as const,
      text: this.sanitizer.bypassSecurityTrustHtml(response.text || ''),
      textComplete: true,
      queryProcessResult: response.queryProcessResult,
      rawResponse: response.rawResponse,
    };

    this.messages.push(messageContent);
    this.scrollToBottomWhitDelay();
  }

  private handleError(error: GenieError | GenieErrorType | any) {
    this.loading = false;
    this.hasError = true;

    // Extraer mensaje del error - priorizar 'text' sobre 'message'
    const baseErrorMessage = error?.text || error?.message || this.translate.instant('AI_CHAT.ERRORS.PROCESSING_ERROR');
    const errorType = error?.type || error?.error || GenieErrorType.UNKNOWN;

    // Capturar detalles técnicos del error para el modal
    const errorDetails = {
      message: baseErrorMessage,
      timestamp: new Date().toISOString(),
      httpStatus: error?.status || error?.originalError?.status,
      httpStatusText: error?.statusText || error?.originalError?.statusText,
      requestUrl: error?.url || error?.originalError?.url,
      type: errorType,
      originalError: error?.originalError || error,
    };

    // Determinar título y sugerencias según el tipo de error
    let userFriendlyTitle = '';
    let suggestions = '';

    // Identificar tipo de error y personalizar
    if (errorType === GenieErrorType.NETWORK || errorType === 'NETWORK') {
      userFriendlyTitle = 'Error de Conexión';
      suggestions = 'Si el problema persiste, contactá al soporte técnico.';
    } else if (errorType === GenieErrorType.TIMEOUT || errorType === 'TIMEOUT') {
      userFriendlyTitle = 'Tiempo de Espera Agotado';
      suggestions = 'Intentá con una pregunta más simple o volvé a intentar más tarde.';
    } else if (errorType === GenieErrorType.AUTH_FAILED || errorType === 'AUTH_FAILED') {
      userFriendlyTitle = 'Error de Autenticación';
      suggestions = 'Recargá la página o contactá al soporte técnico.';
    } else if (errorType === GenieErrorType.RATE_LIMIT || errorType === 'RATE_LIMIT') {
      userFriendlyTitle = 'Límite de Consultas Alcanzado';
      suggestions = 'Esperá unos minutos antes de hacer otra consulta.';
    } else if (errorType === GenieErrorType.WARMING_UP || errorType === 'WARMING_UP') {
      userFriendlyTitle = 'El Motor de Consultas se Está Iniciando';
      suggestions = 'Es normal la primera consulta del día. Volvé a preguntar en unos segundos.';
    } else {
      userFriendlyTitle = 'Error al Procesar la Consulta';
      suggestions = 'Si el problema persiste, contactá al soporte técnico.';
    }

    this.errorMessage = `${userFriendlyTitle}: ${baseErrorMessage}`;

    // Construir mensaje HTML formateado con el mensaje original
    const formattedErrorHtml = `
      <div class="error-message">
        <div style="font-weight: 600; margin-bottom: 8px; color: #d32f2f;">${userFriendlyTitle}</div>
        <div style="margin-bottom: 8px;">${baseErrorMessage}</div>
        <div style="font-size: 13px; color: #666; font-style: italic;">${suggestions}</div>
      </div>
    `;

    const errorMessage: ChatMessage = {
      from: 'ai',
      text: this.sanitizer.bypassSecurityTrustHtml(formattedErrorHtml),
      error: errorType,
      errorDetails: errorDetails,
    };

    this.messages.push(errorMessage);
    this.scrollToBottomWhitDelay();
  }

  openErrorDetailsModal(errorDetails: any): void {
    this.dialog.open(AiDashboardErrorDetailsModalComponent, {
      width: '600px',
      data: { errorDetails },
      panelClass: 'ai-dashboard-error-details-modal',
    });
  }

  private hasValidSchemaQueryResult(queryResult: GenieStatementResponse): boolean {
    return queryResult?.statement_response?.manifest?.schema?.columns?.length > 0;
  }

  private hasValidQueryResult(queryResult: GenieStatementResponse): boolean {
    if (!this.hasValidSchemaQueryResult(queryResult)) return false;

    const { result } = queryResult.statement_response;

    if (!result?.data_typed_array?.length) return false;

    const hasAnyValue = result.data_typed_array.some(row =>
      row.values.some(value => value.str !== undefined || value.num !== undefined || value.bool !== undefined)
    );

    return hasAnyValue;
  }

  hasQuery(message: ChatMessage): boolean {
    return !!message?.rawResponse?.attachments?.[0]?.query;
  }

  getQuery(message: ChatMessage): string {
    const rawQuery = message?.rawResponse?.attachments?.[0]?.query?.query || '';
    const formatted = _formatSqlQuery(rawQuery);
    return formatted;
  }

  copyQuery(message: ChatMessage): void {
    const query = this.getQuery(message);
    if (!query) return;

    navigator.clipboard.writeText(query).then(
      () => {
        _log('Query copiado al portapapeles');
      },
      err => {
        console.error('Error al copiar el query:', err);
      }
    );
  }

  async fakeMirstMsg() {
    // Dummy data for testing - same as original component
    this.messages.push({ from: 'user', text: 'Quiero un listado de todas las estaciones disponibles que están activas' });
    this.loading = true;
    await _timeout(500);
    this.putMsg(dummyDataBricksAiGenieChatResponse);
    // ... rest of dummy data logic
  }

  onChatTypeChange(newType: ChatType): void {
    this.chatTypeChange.emit(newType);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
